import React, { useMemo } from "react";
import moment from "moment";
import { Typography } from "@mui/material";
import {
  PersonAddOutlined,
  EditOutlined,
  AddTaskOutlined,
  EditNoteOutlined,
  DeleteOutlined,
  TrendingUpOutlined,
  SwapVertOutlined,
  NotificationsOutlined,
} from "@mui/icons-material";

import { ActivityTimeline, type ActivityTimelineGroup } from "../v3";
import type { TimelineDayGroup } from "../../utils/crmOperationalDashboard.aggregate";

export type CrmActivityTimelineProps = {
  groups: TimelineDayGroup[];
  loading?: boolean;
};

const iconSx = { fontSize: 20 };

function iconForActivityType(type: string): React.ReactNode {
  switch (type) {
    case "CREATED":
      return <PersonAddOutlined sx={iconSx} />;
    case "UPDATED":
      return <EditOutlined sx={iconSx} />;
    case "TASK_CREATED":
      return <AddTaskOutlined sx={iconSx} />;
    case "TASK_UPDATED":
      return <EditNoteOutlined sx={iconSx} />;
    case "TASK_DELETED":
      return <DeleteOutlined sx={iconSx} />;
    case "TASK_PROGRESS_UPDATED":
      return <TrendingUpOutlined sx={iconSx} />;
    case "PRIORITY_CHANGE":
      return <SwapVertOutlined sx={iconSx} />;
    default:
      return <NotificationsOutlined sx={iconSx} />;
  }
}

export const CrmActivityTimeline = ({ groups, loading }: CrmActivityTimelineProps) => {
  const timelineGroups: ActivityTimelineGroup[] = useMemo(() => {
    return groups.map((g) => ({
      id: g.dateKey,
      heading: g.heading,
      items: g.items.map((item) => ({
        icon: (
          <span className="material-icons-outlined" style={{ fontSize: 20 }}>
            {iconForActivityType(item.type)}
          </span>
        ),
        primary: item.description || item.type,
        secondary: `${item.type.replace(/_/g, " ")} · ${item.actorName}`,
        meta: moment(item.createdAt).format("HH:mm"),
      })),
    }));
  }, [groups]);

  if (loading) {
    return (
      <Typography variant="body2" color="text.secondary">
        Loading activity…
      </Typography>
    );
  }

  return <ActivityTimeline groups={timelineGroups} maxHeight={360} />;
};
